import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ForwardedRef,
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
} from 'react';
import {
  Control,
  useFieldArray,
  useForm,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormWatch,
} from 'react-hook-form';
import { ButtonTypeEnum } from '../../../../../enums/button-type-enum';
import { LabelTypeEnum } from '../../../../../enums/label-enum';
import {
  EmploymentType,
  IWorkExperience,
  WorkType,
} from '../../../../../interfaces/user-work-experience.interface';
import { enumValueToSelectOption } from '../../../../../utils/helper/enum-value-to-select-option-helper';
import { workExperiencesSchema } from '../../../../../validation/work-experience-validation';
import DeleteFileRedIcon from '../../../../Icons/DeleteFileRedIcon';
import CustomButton from '../../../../MUIComponents/CustomButton';
import Input from '../../../../MUIComponents/Input';
import Label from '../../../../MUIComponents/Label';
import CheckBox from './CheckBox';
import TimeLineInputs from './TimeLineInputs';
import { workTypeLabelPlugin } from '../../../../../utils/helper/workTypeEnumValuePlugin';

const DEFAULT_WORK_EXPERIENCE = {
  isActiveAthlete: false,
  title: '',
  employmentType: undefined,
  companyName: '',
  location: '',
  workType: undefined,
  startDate: undefined,
  endDate: undefined,
  activeWork: false,
};

export interface WorkExperienceFormInputs {
  workExperiences: IWorkExperience[];
}

export interface FormRef {
  control: Control<WorkExperienceFormInputs, any>;
  reset: UseFormReset<WorkExperienceFormInputs>;
  watch: UseFormWatch<WorkExperienceFormInputs>;
  handleSubmit: UseFormHandleSubmit<WorkExperienceFormInputs>;
}

interface WorkExperienceFormProps {
  savedWorkExperiences?: IWorkExperience[] | undefined;
  setIsFormChanged?: (flag: boolean) => void;
  onFormTouchChange?: (isTouched: boolean) => void;
}

const WorkExperienceForm = forwardRef<FormRef, WorkExperienceFormProps>(
  (
    {
      savedWorkExperiences,
      setIsFormChanged,
      onFormTouchChange,
    }: WorkExperienceFormProps,
    ref: ForwardedRef<FormRef>
  ) => {
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down('md'));

    const {
      register,
      formState: { errors, isDirty, touchedFields },
      control,
      handleSubmit,
      reset,
      watch,
    } = useForm<WorkExperienceFormInputs>({
      resolver: yupResolver(workExperiencesSchema),
      defaultValues: {
        workExperiences:
          savedWorkExperiences && savedWorkExperiences.length > 0
            ? savedWorkExperiences
            : [DEFAULT_WORK_EXPERIENCE],
      },
      mode: 'onBlur',
      reValidateMode: 'onChange',
    });

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'workExperiences',
    });

    useImperativeHandle(ref, () => ({ control, reset, watch, handleSubmit }));

    const watchedValues = watch();

    useEffect(() => {
      if (!onFormTouchChange) return;

      const hasTouched = Object.keys(touchedFields).length > 0;
      const hasSaved =
        !!savedWorkExperiences && savedWorkExperiences.length > 0;

      // Treat callback value as "can proceed" flag: allow Save when
      // there is either user interaction or previously saved data.
      onFormTouchChange(hasTouched || isDirty || hasSaved);
    }, [
      touchedFields,
      watchedValues.workExperiences,
      isDirty,
      savedWorkExperiences,
      onFormTouchChange,
    ]);

    useEffect(() => {
      if (!setIsFormChanged) return;
      setIsFormChanged(isDirty);
    }, [setIsFormChanged, isDirty]);

    return (
      <>
        {fields.map((item, index) => (
          <Fragment key={item.id}>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
            >
              {savedWorkExperiences && (
                <Label
                  labelType={
                    item?.verified
                      ? LabelTypeEnum.VERIFIED
                      : LabelTypeEnum.UNDER_VERIFICATION
                  }
                >
                  {item?.verified
                    ? LabelTypeEnum.VERIFIED
                    : LabelTypeEnum.UNDER_VERIFICATION}
                </Label>
              )}
              {fields.length > 1 && (
                <IconButton onClick={() => remove(index)}>
                  <DeleteFileRedIcon />
                </IconButton>
              )}
            </Box>

            <Grid
              container
              className="work-experience-form-container"
              sx={{ rowGap: '36px', '& > .MuiGrid-item': { width: 1 } }}
            >
              <Grid item>
                <Box bgcolor="background.light" p="16px 12px" width={1}>
                  <CheckBox
                    name={`workExperiences.${index}.isActiveAthlete`}
                    label="active athlete"
                    control={control}
                  />
                </Box>
              </Grid>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: '36px', md: '22px' }}
                sx={{ width: 1 }}
              >
                <Grid item xs={12} md={6}>
                  <Input
                    caption="Title"
                    containerFlex="1"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter title"
                    register={register(`workExperiences.${index}.title`)}
                    error={!!errors.workExperiences?.[index]?.title}
                    helperText={
                      errors.workExperiences?.[index]?.title
                        ? errors.workExperiences?.[index]?.title?.message
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Input
                      select
                      selectItems={Object.values(EmploymentType).map((val) => ({
                        label: enumValueToSelectOption(val),
                        value: val,
                      }))}
                      defaultValue={
                        savedWorkExperiences?.[index]?.employmentType
                      }
                      containerFlex="1"
                      caption="Employment type"
                      variant="outlined"
                      fullWidth
                      placeholder="Choose one"
                      register={register(
                        `workExperiences.${index}.employmentType`
                      )}
                      error={!!errors.workExperiences?.[index]?.employmentType}
                      helperText={
                        errors.workExperiences?.[index]?.employmentType
                          ?.message || ''
                      }
                      sxBox={{ '& .MuiOutlinedInput-root': { p: '0px' } }}
                    />
                  </FormControl>
                </Grid>
              </Stack>

              <Grid item>
                <Input
                  caption="Company name"
                  containerFlex="1"
                  variant="outlined"
                  fullWidth
                  placeholder="Enter company name"
                  register={register(`workExperiences.${index}.companyName`)}
                  error={!!errors.workExperiences?.[index]?.companyName}
                  helperText={
                    errors.workExperiences?.[index]?.companyName
                      ? errors.workExperiences[index]?.companyName?.message
                      : ''
                  }
                />
              </Grid>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: '36px', md: '22px' }}
                sx={{ width: 1 }}
              >
                <Grid item xs={12} md={6}>
                  <Input
                    caption="Location"
                    containerFlex="1"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter location"
                    register={register(`workExperiences.${index}.location`)}
                    error={!!errors.workExperiences?.[index]?.location}
                    helperText={
                      errors.workExperiences?.[index]?.location
                        ? errors.workExperiences?.[index]?.location?.message
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Input
                      select
                      selectItems={Object.values(WorkType).map((val) => ({
                        label: enumValueToSelectOption(
                          workTypeLabelPlugin(val)
                        ),
                        value: val,
                      }))}
                      defaultValue={savedWorkExperiences?.[index]?.workType}
                      containerFlex="1"
                      caption="Work type"
                      variant="outlined"
                      fullWidth
                      placeholder="Choose one"
                      register={register(`workExperiences.${index}.workType`)}
                      error={!!errors.workExperiences?.[index]?.workType}
                      helperText={
                        errors.workExperiences?.[index]?.workType?.message || ''
                      }
                      sxBox={{ '& .MuiOutlinedInput-root': { p: '0px' } }}
                    />
                  </FormControl>
                </Grid>
              </Stack>

              <Grid item>
                <Box
                  display="flex"
                  gridColumn="span 2"
                  rowGap={mobileView ? '1.75rem' : '36px'}
                  columnGap="16px"
                  mt="36px"
                >
                  <Input
                    caption="Describe your responsibilities"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Add a few words about your experience"
                    register={register(
                      `workExperiences.${index}.responsibilities`
                    )}
                    error={!!errors.workExperiences?.[index]?.responsibilities}
                    helperText={
                      errors.workExperiences?.[index]?.responsibilities
                        ?.message || ''
                    }
                    containerFlex="1"
                  />
                </Box>
              </Grid>

              <Grid item container sx={{ width: 1 }}>
                <Grid item sx={{ width: 1 }}>
                  <TimeLineInputs
                    index={index}
                    label="Employment period"
                    control={control}
                    errors={errors}
                  />
                </Grid>
              </Grid>

              <Grid item>
                <CheckBox
                  name={`workExperiences.${index}.activeWork`}
                  label="I'm currently working in this role"
                  control={control}
                />
              </Grid>
            </Grid>

            {savedWorkExperiences && index !== fields.length - 1 && <Divider />}
          </Fragment>
        ))}

        <CustomButton
          buttonWidth="100%"
          variantType={ButtonTypeEnum.SECONDARY}
          sx={{ borderColor: 'background.dark', mt: '36px', height: 58 }}
          onClick={() =>
            append(DEFAULT_WORK_EXPERIENCE as unknown as IWorkExperience)
          }
          disabled={fields.length === 5}
          type="button"
        >
          add work experience
        </CustomButton>
      </>
    );
  }
);

WorkExperienceForm.displayName = 'WorkExperienceForm';

export default WorkExperienceForm;
